import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useActiveFeatureFlags } from 'posthog-js/react';
import { useAuthStore } from '@/store/authStore';
import { useAnnouncementDismissStates } from './useAnnouncementDismissStates';
import { useAnnouncementDelay } from './useAnnouncementDelay';
import { ANNOUNCEMENTS, getActiveAnnouncements } from '../config/announcements';
import type { AnnouncementConfig } from '../types';

interface AnnouncementWithDismissState extends AnnouncementConfig {
  isDismissed: boolean;
  isLoading: boolean;
  dismiss: () => void;
}

export interface WhatsNewState {
  /** All active announcements with their dismiss state */
  allAnnouncements: AnnouncementWithDismissState[];
  /** Only undismissed announcements (for modal) */
  undismissedAnnouncements: AnnouncementConfig[];
  /** Current step index in the modal */
  currentIndex: number;
  /** Total undismissed count */
  totalCount: number;
  /** Current announcement being viewed */
  currentAnnouncement: AnnouncementConfig | undefined;
  /** Navigation state */
  hasNext: boolean;
  hasPrevious: boolean;
  /** Whether any announcements are still loading */
  isLoading: boolean;
  /** Actions */
  next: () => void;
  previous: () => void;
  goToIndex: (index: number) => void;
  dismissCurrent: () => void;
  dismissAll: () => void;
  /** Track modal opened */
  trackModalOpened: () => void;
  /** Track step viewed */
  trackStepViewed: () => void;
}

export function useWhatsNew(): WhatsNewState {
  const { roles } = useAuthStore();
  const [currentIndex, setCurrentIndex] = useState(0);
  const modalOpenedTrackedRef = useRef(false);

  // Reactive flag list — triggers re-evaluation when PostHog loads real flags from the server.
  // Without this, the useMemo below would only run once with the bootstrap values (all false)
  // and never re-run when the actual flag values arrive async.
  const activeFlags = useActiveFeatureFlags();

  // Check if announcements should be suppressed due to onboarding
  const { shouldSuppressAnnouncements, wentThroughOnboarding, isLoading: isLoadingOnboarding } =
    useAnnouncementDelay();

  // Get announcement IDs for the dismiss hook
  const announcementIds = useMemo(() => ANNOUNCEMENTS.map((a) => a.id), []);

  // Single hook call for all dismiss states
  const dismissStates = useAnnouncementDismissStates(announcementIds, 'modal');

  // Filter by feature flag using non-hook API + roles
  const flagEnabledAnnouncements = useMemo(() => {
    return getActiveAnnouncements().filter((announcement) => {
      // Only include announcements configured to show as modal
      if (!announcement.showModal) return false;

      // Check feature flag using non-hook API
      if (!posthog.isFeatureEnabled(announcement.featureFlag)) return false;

      // Check role targeting
      if (announcement.targetRoles && announcement.targetRoles.length > 0) {
        if (!announcement.targetRoles.some((role) => roles.includes(role))) {
          return false;
        }
      }

      // Permanently exclude existingUsersOnly announcements for users who went through onboarding
      if (announcement.existingUsersOnly && wentThroughOnboarding) {
        return false;
      }

      return true;
    });
    // activeFlags dependency ensures this re-evaluates when PostHog loads flags from the server
  }, [roles, activeFlags, wentThroughOnboarding]);

  // Combine announcements with their dismiss state
  const allAnnouncements: AnnouncementWithDismissState[] = useMemo(() => {
    return flagEnabledAnnouncements.map((announcement) => {
      const state = dismissStates[announcement.id];
      return {
        ...announcement,
        isDismissed: state?.isDismissed ?? false,
        isLoading: state?.isLoading ?? true,
        dismiss: state?.dismiss ?? (async () => {}),
      };
    });
  }, [flagEnabledAnnouncements, dismissStates]);

  // Get only undismissed announcements for the modal
  // Suppress for new users or users within the 24-hour delay period after onboarding
  const undismissedAnnouncements = useMemo(() => {
    if (shouldSuppressAnnouncements) {
      return [];
    }
    return allAnnouncements.filter((a) => !a.isDismissed && !a.isLoading);
  }, [allAnnouncements, shouldSuppressAnnouncements]);

  // Check if still loading (including onboarding preference check)
  const isLoading = isLoadingOnboarding || allAnnouncements.some((a) => a.isLoading);

  // Current announcement
  const currentAnnouncement = undismissedAnnouncements[currentIndex];
  const totalCount = undismissedAnnouncements.length;

  // Navigation
  const hasNext = currentIndex < totalCount - 1;
  const hasPrevious = currentIndex > 0;

  const next = useCallback(() => {
    if (hasNext) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [hasNext]);

  const previous = useCallback(() => {
    if (hasPrevious) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [hasPrevious]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalCount) {
        setCurrentIndex(index);
      }
    },
    [totalCount]
  );

  // Dismiss current announcement
  const dismissCurrent = useCallback(() => {
    if (!currentAnnouncement) return;

    const announcementState = allAnnouncements.find((a) => a.id === currentAnnouncement.id);
    if (announcementState) {
      // Track dismissal
      try {
        posthog.capture('whats_new_step_dismissed', {
          announcement_id: currentAnnouncement.id,
          step_index: currentIndex,
        });
      } catch (e) {
        console.error('Error tracking step dismiss:', e);
      }

      announcementState.dismiss();

      // If we dismissed the last one in the list, go back one
      if (currentIndex >= totalCount - 1 && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    }
  }, [currentAnnouncement, allAnnouncements, currentIndex, totalCount]);

  // Dismiss all announcements
  const dismissAll = useCallback(() => {
    // Track dismissal
    try {
      posthog.capture('whats_new_all_dismissed', {
        total_dismissed: undismissedAnnouncements.length,
      });
    } catch (e) {
      console.error('Error tracking all dismiss:', e);
    }

    // Dismiss all undismissed announcements (mutations are optimistic)
    undismissedAnnouncements.forEach((announcement) => {
      const state = allAnnouncements.find((a) => a.id === announcement.id);
      state?.dismiss();
    });

    setCurrentIndex(0);
  }, [undismissedAnnouncements, allAnnouncements]);

  // Track modal opened (call once when modal is shown)
  const trackModalOpened = useCallback(() => {
    if (modalOpenedTrackedRef.current) return;
    modalOpenedTrackedRef.current = true;

    try {
      posthog.capture('whats_new_modal_opened', {
        total_announcements: allAnnouncements.length,
        undismissed_count: undismissedAnnouncements.length,
      });
    } catch (e) {
      console.error('Error tracking modal opened:', e);
    }
  }, [allAnnouncements.length, undismissedAnnouncements.length]);

  // Track step viewed
  const trackStepViewed = useCallback(() => {
    if (!currentAnnouncement) return;

    try {
      posthog.capture('whats_new_step_viewed', {
        announcement_id: currentAnnouncement.id,
        step_index: currentIndex,
        total_steps: totalCount,
      });
    } catch (e) {
      console.error('Error tracking step viewed:', e);
    }
  }, [currentAnnouncement, currentIndex, totalCount]);

  // Reset modal tracked state when all are dismissed
  useEffect(() => {
    if (undismissedAnnouncements.length === 0) {
      modalOpenedTrackedRef.current = false;
    }
  }, [undismissedAnnouncements.length]);

  return {
    allAnnouncements,
    undismissedAnnouncements,
    currentIndex,
    totalCount,
    currentAnnouncement,
    hasNext,
    hasPrevious,
    isLoading,
    next,
    previous,
    goToIndex,
    dismissCurrent,
    dismissAll,
    trackModalOpened,
    trackStepViewed,
  };
}
